import { DateTime } from 'luxon'
import { computed, ref, toValue } from 'vue'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import type { CalendarDay } from '~/interfaces/CalendarDay'
import { buildMonthCalendarDays } from '~/utils/buildMonthCalendarDays'

interface UseMonthCalendarReturn {
  calendarDays: ComputedRef<CalendarDay[]>
  currentMonthYear: ComputedRef<string>
  nextMonth: () => void
  previousMonth: () => void
  showMonthOf: (date: Date) => void
}

/** Month navigation and the six-week day grid for the month being viewed, in the calendar's timezone. */
export const useMonthCalendar = (options: {
  isSelected: (date: string) => boolean
  timezone: MaybeRefOrGetter<string>
}): UseMonthCalendarReturn => {
  const visibleDate = ref(DateTime.now().toJSDate())

  const currentMonthYear = computed(() =>
    DateTime.fromJSDate(visibleDate.value).setZone(toValue(options.timezone)).toFormat('LLLL yyyy')
  )

  const calendarDays = computed<CalendarDay[]>(() =>
    buildMonthCalendarDays(visibleDate.value, toValue(options.timezone), options.isSelected)
  )

  const shiftMonth = (months: number) => {
    visibleDate.value = DateTime.fromJSDate(visibleDate.value)
      .setZone(toValue(options.timezone))
      .startOf('month')
      .plus({ months })
      .toJSDate()
  }

  const previousMonth = () => {
    shiftMonth(-1)
  }

  const nextMonth = () => {
    shiftMonth(1)
  }

  const showMonthOf = (date: Date) => {
    visibleDate.value = date
  }

  return { calendarDays, currentMonthYear, nextMonth, previousMonth, showMonthOf }
}
